import React, { useState, useEffect, useRef } from 'react';
import useFilterPersistence from '../hooks/useFilterPersistence';
import { Container, Button, Spinner, Alert, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getOrdersGeolocation, getOrders, getCarriers } from '../api';
import OrderListFilter from '../components/Orders/OrderListFilter';
import OrderProductsSummary from '../components/Global/OrderProductsSummary';
import CarrierAssigner from '../components/Global/CarrierAssigner';
import OrdersMap from '../components/OrdersMap';
import { formatDateToYYYYMMDD } from '../utils/DateUtils';

const OrdersMapPage = () => {
  const [locations, setLocations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [hiddenOrders, setHiddenOrders] = useState(new Set());
  const [carriers, setCarriers] = useState([]);
  
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateToYYYYMMDD(tomorrow);
  };

  const [filters, setFilters] = useFilterPersistence('ordersMap_filters', (() => {
    const tomorrow = getTomorrowDate();
    return {
      orderDateFrom: '',
      orderDateTo: '',
      deliveryDateFrom: tomorrow,
      deliveryDateTo: tomorrow,
      salesRepId: [],
      carrierId: [],
      incidentTypeId: '',
      incidentActionId: '',
      clientId: '',
      status: '',
      hasSpecialProducts: null,
      zipCode: '',
      city: '',
    };
  })());

  useEffect(() => {
    const loadCarriers = async () => {
      try {
        const carriersData = await getCarriers();
        setCarriers(carriersData);
      } catch (error) {
        console.error('Error loading carriers:', error);
      }
    };
    
    loadCarriers();
  }, []);

  useEffect(() => {
    if (filters.deliveryDateFrom || filters.deliveryDateTo || filters.carrierId.length > 0) {
      loadOrderLocations();
    } else {
      setLocations([]);
      setOrders([]);
    }
  }, [filters]);

  const loadOrderLocations = async () => {
    if (!filters.deliveryDateFrom && !filters.deliveryDateTo && filters.carrierId.length === 0) {
      setLocations([]);
      setOrders([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setLocations([]);
      setOrders([]);
      
      const ordersResponse = await getOrders(filters, 1, 1000);
      const fetchedOrders = ordersResponse.items || [];
      
      if (fetchedOrders.length === 0) {
        setLocations([]);
        setOrders([]);
        return;
      }
      
      setOrders(fetchedOrders);
      setLocations([]);
      
      const orderIds = fetchedOrders.map(order => order.id);
      const response = await getOrdersGeolocation(orderIds);
      
      const processedLocations = (response || []).map((location) => {
        const order = fetchedOrders.find(o => o.id === location.orderId);
        let fullAddress = 'Dirección no disponible';
        
        if (location.address && typeof location.address === 'object') {
          const addr = location.address;
          const parts = [
            addr.address,
            addr.city,
            addr.state,
            addr.zipCode,
            addr.country
          ].filter(part => part && typeof part === 'string' && part.trim() !== '');
          
          fullAddress = parts.join(', ');
        }
        
        return {
          ...location,
          fullAddress,
          order
        };
      });
      
      setLocations(processedLocations);
      
      // Ocultar automáticamente pedidos finalizados
      const finalizedOrderIds = processedLocations
        .filter(loc => loc.order?.status === 'Entregado' || loc.order?.status === 'Albarán firmado')
        .map(loc => loc.orderId);
      setHiddenOrders(new Set(finalizedOrderIds));
    } catch (err) {
      setError('Error al cargar los pedidos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = setFilters;

  const handlePrint = () => {
    const printEvent = new CustomEvent('beforePrint');
    window.dispatchEvent(printEvent);
    
    setTimeout(() => {
      const finalAdjustEvent = new CustomEvent('finalPrintAdjust');
      window.dispatchEvent(finalAdjustEvent);
      
      setTimeout(() => {
        window.print();
      }, 500);
    }, 1000);
  };

  const handleResetMap = () => {
    if (hiddenOrders.size === 0 && locations.length > 0) {
      // Si no hay ninguna oculta, ocultar y mostrar rápidamente la primera
      const firstOrderId = locations.find(loc => loc.success)?.orderId;
      if (firstOrderId) {
        setHiddenOrders(new Set([firstOrderId]));
        setTimeout(() => setHiddenOrders(new Set()), 100);
      }
    } else {
      setHiddenOrders(new Set());
    }
  };

  const handleLocationClick = (location, number) => {
    const convertedLocation = {
      ...location,
      latitude: location.latitude,
      longitude: location.longitude
    };
    
    const mapEvent = new CustomEvent('focusLocation', {
      detail: { location: convertedLocation, number }
    });
    window.dispatchEvent(mapEvent);
  };

  const toggleLocationVisibility = (orderId) => {
    setHiddenOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleCarrierChange = (orderIds, newCarrier) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const updatedOrders = orders.map(order => {
      if (ids.includes(order.id)) {
        return { ...order, carrier: newCarrier };
      }
      return order;
    });
    setOrders(updatedOrders);
    
    // Actualizar también las ubicaciones
    const updatedLocations = locations.map(location => {
      if (ids.includes(location.orderId)) {
        return {
          ...location,
          order: { ...location.order, carrier: newCarrier }
        };
      }
      return location;
    });
    setLocations(updatedLocations);
  };





  const validLocations = locations.filter(loc => 
    loc.success && loc.latitude != null && loc.longitude != null && 
    !isNaN(loc.latitude) && !isNaN(loc.longitude) && !hiddenOrders.has(loc.orderId)
  ).map(loc => ({
    ...loc,
    latitude: loc.latitude,
    longitude: loc.longitude,
    fullAddress: typeof loc.fullAddress === 'string' ? loc.fullAddress : 'Dirección no disponible'
  }));

  return (
    <Container fluid className="mt-4">
      <style>
        {`
          .custom-number-marker, .custom-cluster-marker {
            background: transparent !important;
            border: none !important;
          }
          
          .location-item:hover {
            background-color: #f8f9fa;
            border-radius: 4px;
          }
          
          .location-item.clickable {
            transition: background-color 0.2s ease;
          }
          
          .location-item.clickable:hover {
            background-color: #e9ecef;
          }
          
          @media print {
            .no-print { display: none !important; }
            .print-title { font-size: 24px; margin-bottom: 20px; }
            body { margin: 0; padding: 0; }
            .container-fluid { max-width: 100% !important; padding: 0 !important; }
            .row { margin: 0 !important; }
            .col-12, .col-md-6, .col-lg-4 { padding: 0 !important; width: 100% !important; max-width: 100% !important; flex: none !important; }
            .card { border: 1px solid #000 !important; margin: 10px 0 !important; page-break-inside: avoid; }
            .card-body { padding: 8px !important; }
            #map-container { 
              height: 600px !important; 
              width: 1028px !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              zoom: 0.75 !important;
            }
            .leaflet-container { 
              height: 600px !important; 
              width: 1028px !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .leaflet-control-container { display: none !important; }
            .card { margin: 5px 0 !important; }
            .card-body { padding: 5px !important; }
            .mb-4, .mb-3 { margin-bottom: 10px !important; }
            small { font-size: 10px !important; line-height: 1.2 !important; }
            h6 { font-size: 12px !important; }
            h5 { font-size: 14px !important; }
            @page { margin: 0.1in; size: portrait; }
          }
        `}
      </style>
      
      <div className="mb-4 no-print">
        <h1 className="mb-1">Mapa de Pedidos</h1>
      </div>

      <div className="no-print">
        <OrderListFilter 
          filters={filters} 
          setFilters={handleFiltersChange} 
          type="transport"
          carriers={carriers}
        />
      </div>

      <h1 className="print-title d-none d-print-block">Mapa de Pedidos</h1>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div className="row">
        <div className="col-lg-8 mb-4">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center no-print">
              <h6 className="mb-0">Mapa</h6>
              <div className="d-flex gap-2">
                <OverlayTrigger placement="bottom" overlay={<Tooltip>Reiniciar Mapa</Tooltip>}>
                  <Button size="sm" variant="secondary" onClick={handleResetMap}>
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger placement="bottom" overlay={<Tooltip>Imprimir</Tooltip>}>
                  <Button size="sm" variant="primary" onClick={handlePrint}>
                    <i className="bi bi-printer"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-center align-items-center p-3">
              <div id="map-container" style={{ height: '600px', width: '100%', margin: '0 auto', position: 'relative', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" />
                    <span className="ms-2">Cargando pedidos...</span>
                  </div>
                ) : (
                  <OrdersMap locations={validLocations} resetTrigger={resetTrigger} />
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
        
        <div className="col-lg-4 mb-4">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Pedidos</h5>
              <small className="text-muted">
                {locations.filter(loc => loc.success).length} de {locations.length} con ubicación
                {locations.some(loc => !loc.success) && (
                  <span className="text-warning ms-1">
                    <i className="bi bi-exclamation-triangle"></i>
                  </span>
                )}
              </small>
            </Card.Header>
            <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {!filters.deliveryDateFrom && !filters.deliveryDateTo && filters.carrierId.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-funnel" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2">Aplica filtros para ver los pedidos en el mapa</p>
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-geo-alt" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2">No hay pedidos para mostrar</p>
                </div>
              ) : (
                locations.map((location, index) => {
                  const order = location.order;
                  return (
                    <div key={location.orderId} className={`p-3 border-bottom location-item ${location.success ? 'clickable' : ''} ${hiddenOrders.has(location.orderId) ? 'opacity-50' : ''}`} style={{ cursor: location.success ? 'pointer' : 'default', borderLeft: order?.isImportant ? '5px solid #ff6b35' : 'none', backgroundColor: order?.hasSpecialProducts ? '#f3e5f5' : (location.address?.exactGeoMatch === false ? '#fff3cd' : 'inherit') }} onClick={() => location.success && !hiddenOrders.has(location.orderId) && handleLocationClick(location, index + 1)}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center">
                          {location.success && !hiddenOrders.has(location.orderId) && (
                            <span className="badge bg-primary me-2" style={{ minWidth: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                              {locations.filter(l => l.success && !hiddenOrders.has(l.orderId)).findIndex(l => l.orderId === location.orderId) + 1}
                            </span>
                          )}
                          {hiddenOrders.has(location.orderId) && (
                            <span className="badge bg-secondary me-2" style={{ minWidth: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                              <i className="bi bi-eye-slash" style={{ fontSize: '10px' }}></i>
                            </span>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
                              <h6 className="mb-0">{order?.externalId || `Pedido #${location.orderId}`}</h6>
                              {order?.isImportant && (
                                <span className="badge" style={{ background: 'linear-gradient(45deg, #ff6b35, #f7931e)', color: 'white', fontSize: '10px', padding: '2px 4px' }} title="Pedido importante">
                                  ⭐
                                </span>
                              )}
                              {order?.hasSpecialProducts && (
                                <span className="badge" style={{ backgroundColor: '#9c27b0', color: 'white', fontSize: '10px', padding: '2px 4px' }} title="Contiene productos especiales">
                                  🔷
                                </span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              {order?.status && (
                                <span className={`badge ${order.status === 'Entregado' || order.status === 'INCIDENCIA' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '10px' }} title={`Estado: ${order.status}`}>
                                  {order.status}
                                </span>
                              )}
                              {location.address?.exactGeoMatch === false && (
                                <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }} title="Ubicación aproximada">
                                  <i className="bi bi-exclamation-triangle me-1"></i>Aprox.
                                </span>
                              )}
                            </div>
                            {order && (
                              <small className="text-muted">
                                {order.client?.name} {order.client?.lastName1} - {order.totalAmount}€
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          <Button 
                            size="sm" 
                            variant={hiddenOrders.has(location.orderId) ? "outline-success" : "outline-secondary"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLocationVisibility(location.orderId);
                            }}
                            title={hiddenOrders.has(location.orderId) ? "Mostrar en mapa" : "Ocultar del mapa"}
                          >
                            <i className={`bi ${hiddenOrders.has(location.orderId) ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                          </Button>
                          <a href={`/orders/${location.orderId}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" onClick={(e) => e.stopPropagation()}>
                            <i className="bi bi-box-arrow-up-right"></i>
                          </a>
                        </div>
                      </div>
                      
                      {order && (
                        <div className="mb-2">
                          <OrderProductsSummary order={order} />
                          <div className="mt-2 d-flex align-items-center">
                            <small className="text-muted me-2">Transportista:</small>
                            <CarrierAssigner order={order} onCarrierChange={handleCarrierChange} />
                          </div>
                        </div>
                      )}
                      
                      {location.success ? (
                        <div>
                          {location.fullAddress && (
                            <small className={`d-block mb-1 ${location.address?.exactGeoMatch === false ? 'text-dark fw-bold' : 'text-muted'}`}>
                              <i className="bi bi-geo-alt me-1"></i>{location.fullAddress}
                            </small>
                          )}
                          {!hiddenOrders.has(location.orderId) ? (
                            <small className="text-info">
                              <i className="bi bi-cursor-fill me-1"></i>Clic para enfocar
                            </small>
                          ) : (
                            <small className="text-muted">
                              <i className="bi bi-eye-slash me-1"></i>Oculto del mapa
                            </small>
                          )}
                        </div>
                      ) : (
                        <small className="text-danger">
                          <i className="bi bi-x-circle me-1"></i>Sin ubicación
                        </small>
                      )}
                    </div>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </div>
      </div>


    </Container>
  );
};



export default OrdersMapPage;